import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useDocument } from '@nandorojo/swr-firestore';
import styled from 'styled-components';
import Switch from '@components/dataEntry/switch';
import RangeSlider from '@components/dataEntry/rangeSlider';
import Button from '@components/dataEntry/button/Button';
import FileUploadInput from '@components/dataEntry/upload/FileUploadInput';
import {
  LabelDescription,
  SetGrid,
  SettingContainer,
  SettingTitle,
} from '@styles/common/SettingPage';
import { notificationSettingsState } from '@atom/setting/notification';
import { SettingCollectionPath } from '@typings/collections.enum';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import {
  removeSoundFile,
  updateSoundFile,
} from '@store/firebase/setting/set-soundEffect';

export const LOCAL_FALLBACK_URL = '/assets/sounds/message_sent.mp3';

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const VolumeLabel = styled.span`
  font-size: 13px;
  color: var(--subTextColor);
  min-width: 36px;
  text-align: right;
`;

const ActiveBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  background: var(--primaryColor);
  color: var(--buttonTextColor);
  margin-left: 8px;
  vertical-align: middle;
`;

const PreviewBtn = styled.button`
  height: 40px;
  padding: 0 12px;
  border-radius: var(--buttonBorderRadius);
  background-color: var(--cardBackgroundColor);
  color: var(--textColor);
  border: 1px solid var(--primaryColor);
  font-size: 13px;
  white-space: nowrap;
  transition: background-color 0.2s, color 0.2s;
  cursor: var(--hoverCursorURL), pointer;

  &:hover {
    background-color: var(--primaryColor);
    color: var(--buttonTextColor);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

function NotificationSoundPage() {
  const [settings, setSettings] = useRecoilState(notificationSettingsState);
  const audioContextRef = useRef<AudioContext | null>(null);

  const { data: soundEffect } = useDocument<ISoundEffect>(
    SettingCollectionPath.SOUNDEFFECT,
    { listen: true },
  );

  const [notificationSoundFile, setNotificationSoundFile] =
    useState<File | null>();
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  // 업로드된 Firestore URL 우선, 없으면 로컬 fallback
  const effectiveSoundUrl =
    soundEffect?.notificationSound?.url || LOCAL_FALLBACK_URL;
  const isUsingFirestore = !!soundEffect?.notificationSound?.url;

  // 언마운트 시 미리듣기 오디오 정지
  useEffect(() => {
    return () => {
      previewAudioRef.current?.pause();
    };
  }, []);

  const handlePreview = useCallback(() => {
    const playBeep = (ctx: AudioContext) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      gainNode.gain.setValueAtTime(
        (settings.volume / 100) * 0.3,
        ctx.currentTime,
      );
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    };

    const getOrCreateCtx = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }
      return audioContextRef.current;
    };

    if (effectiveSoundUrl) {
      const audio = new Audio(effectiveSoundUrl);
      audio.volume = Math.max(0, Math.min(1, settings.volume / 100));
      previewAudioRef.current = audio;
      audio.play().catch(() => {
        if (!settings.useFallbackBeep) return;
        const ctx = getOrCreateCtx();
        if (ctx.state === 'suspended') ctx.resume().then(() => playBeep(ctx));
        else playBeep(ctx);
      });
    } else if (settings.useFallbackBeep) {
      const ctx = getOrCreateCtx();
      if (ctx.state === 'suspended') ctx.resume().then(() => playBeep(ctx));
      else playBeep(ctx);
    }
  }, [effectiveSoundUrl, settings]);

  const handleVolumeChange = useCallback(
    (value: string | number) => {
      setSettings((prev) => ({ ...prev, volume: Number(value) }));
    },
    [setSettings],
  );

  const handleToggleFallback = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      useFallbackBeep: !prev.useFallbackBeep,
    }));
  }, [setSettings]);

  const handleSoundSave = useCallback(() => {
    if (!notificationSoundFile) return;
    updateSoundFile({
      key: 'notificationSound',
      file: notificationSoundFile,
      prevURL: soundEffect?.notificationSound?.url || '',
    });
  }, [notificationSoundFile, soundEffect]);

  const handleSoundRemove = useCallback(() => {
    removeSoundFile({
      key: 'notificationSound',
      prevURL: soundEffect?.notificationSound?.url || '',
    });
  }, [soundEffect]);

  // soundEffect 문서가 없어도 로컬 fallback으로 UI를 그대로 표시
  const isLoadingSoundEffect = soundEffect === undefined;

  return (
    <>
      <SettingContainer>
        <SettingTitle>
          볼륨 설정
          <LabelDescription>
            알림 소리의 볼륨을 조절합니다. (0 ~ 100)
          </LabelDescription>
        </SettingTitle>
        <SetGrid>
          <label>알림 볼륨</label>
          <ButtonRow>
            <RangeSlider
              id="notification-volume"
              value={settings.volume}
              onChange={handleVolumeChange}
              min={0}
              max={100}
              step={1}
              showInput={false}
            />
            <VolumeLabel>{settings.volume}%</VolumeLabel>
          </ButtonRow>
        </SetGrid>
      </SettingContainer>

      <SettingContainer>
        <SettingTitle>
          알림 사운드 파일
          <LabelDescription>
            알림 사운드 mp3 파일을 업로드합니다. 업로드된 파일이 최우선으로
            사용됩니다.
          </LabelDescription>
        </SettingTitle>
        <SetGrid>
          <label>알림음 파일</label>
          <ButtonRow>
            <PreviewBtn
              onClick={handlePreview}
              disabled={!settings.soundEnabled}
              title="테스트 재생"
            >
              🔊 테스트 재생
            </PreviewBtn>
            <FileUploadInput
              id="notification-sound-upload"
              accept=".mp3,audio/*"
              prevFile={soundEffect?.notificationSound?.url || ''}
              prevFileName={soundEffect?.notificationSound?.name || ''}
              setSelectedFile={setNotificationSoundFile}
              deleteFile={handleSoundRemove}
            />
            <Button
              onClick={handleSoundSave}
              size="sm"
              disabled={!notificationSoundFile || isLoadingSoundEffect}
            >
              저장
            </Button>
          </ButtonRow>
        </SetGrid>
      </SettingContainer>

      <SettingContainer>
        <SettingTitle>
          비프음 Fallback
          <LabelDescription>
            사운드 파일 재생에 실패했을 때 브라우저 내장 비프음(800Hz)을 대신
            재생합니다.
          </LabelDescription>
        </SettingTitle>
        <SetGrid>
          <label>비프음 Fallback 사용</label>
          <Switch
            isChecked={settings.useFallbackBeep}
            handleSwitch={handleToggleFallback}
          />
        </SetGrid>
      </SettingContainer>
    </>
  );
}

export default React.memo(NotificationSoundPage);
